import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useAppState from 'context/useAppState';
import { getPathSlug, resolveFirstAccessiblePath } from 'utils/helper';
import { menuConfig } from 'utils/menuConfig';

// Halaman persetujuan manager BAST Out — dikecualikan dari routing berbasis menuConfig
const MANAGER_APPROVAL_PATH = '/bast-out-manager/';

export default function AuthRoute() {
  const { isAuthenticated, data } = useAppState((state) => state.auth);
  const { productType } = useAppState((state) => state);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [hasRedirected, setHasRedirected] = useState(false);

  const redirectLink = searchParams.get('continue');
  const scopes = data?.scopes || [];
  const productTypes = productType.productTypes || [];

  useEffect(() => {
    if (!isAuthenticated) return;
    if (hasRedirected) return;

    // Halaman TTD Manager BAST Out diakses lewat link yang dibagikan manual dan tidak
    // punya entri di menuConfig, jadi pengecekan scope di bawah pasti gagal. Honor-kan
    // ?continue-nya lebih dulu supaya manager tidak nyasar ke menu default setelah login.
    if (redirectLink?.startsWith(MANAGER_APPROVAL_PATH)) {
      navigate(redirectLink, { replace: true, state: location });
      setHasRedirected(true);
      return;
    }

    // Role Security tidak memiliki scope/menu admin (menuConfig), langsung ke modul BAST
    if (data?.user?.role_name === 'Security') {
      navigate('/bast-security', { replace: true });
      setHasRedirected(true);
      return;
    }

    // User eksternal (is_external_flag true) hanya punya akses Report Adira → default ke Penerimaan Unit
    if (data?.user?.is_external_flag === true) {
      navigate('/report-seller/unit-acceptance', { replace: true });
      setHasRedirected(true);
      return;
    }

    if (!scopes.length) return;

    // 1️⃣ Ambil semua permission
    const allowedSlugs = scopes.filter((s) => s.permission);

    if (!allowedSlugs.length) {
      navigate('/no-access', { replace: true });
      setHasRedirected(true);
      return;
    }

    // 2️⃣ Convert slug → sidebar path
    const allowedPaths = allowedSlugs.map((s) => getPathSlug(s.slug)).filter(Boolean);

    // 3️⃣ Ambil menu dari menuConfig
    const allowedMenus = menuConfig.filter((menu) =>
      allowedPaths.includes(menu.link.replace('/', '')),
    );

    if (!allowedMenus.length) {
      navigate('/no-access', { replace: true });
      setHasRedirected(true);
      return;
    }

    const firstMenu = allowedMenus[0];
    const needsInventory = firstMenu.link === '/inventory';

    // 4️⃣ Inventory harus tunggu productTypes
    if (needsInventory && !productTypes.length) return;

    // 5️⃣ Handle ?continue
    if (redirectLink) {
      const targetPath = redirectLink;

      const canAccess = allowedMenus.some((menu) => {
        if (!menu.subMenu) {
          return targetPath === menu.link;
        }
        return targetPath.startsWith(menu.link + '/');
      });

      if (canAccess) {
        navigate(redirectLink, { replace: true, state: location });
        setHasRedirected(true);
        return;
      }
    }

    const currentPath = location.pathname;

    // "/" dan "/login" tidak pernah valid
    if (currentPath !== '/' && !currentPath.startsWith('/login')) {
      const isValid = allowedMenus.some((menu) => {
        if (!menu.subMenu) {
          return currentPath === menu.link;
        }
        return currentPath.startsWith(menu.link + '/');
      });

      if (isValid) {
        setHasRedirected(true);
        return;
      }
    }

    // 6️⃣ Redirect ke menu pertama yang benar
    const redirectPath = resolveFirstAccessiblePath(firstMenu, productTypes);

    navigate(redirectPath, { replace: true });
    setHasRedirected(true);
  }, [
    isAuthenticated,
    data?.user?.role_name,
    data?.user?.is_external_flag,
    scopes,
    productTypes,
    redirectLink,
    location.pathname,
    hasRedirected,
  ]);

  return (
    <Suspense fallback="...">
      <Outlet />
    </Suspense>
  );
}
